import { DateTime } from 'luxon';

import { AccessorialCode, ZonedDateTimeRange } from '@rygen/rygen-ui/model';
import { loadLuxonDateTime } from '@rygen/rygen-ui/util';

import i18n from '@/i18n';

import { EmbeddableContact } from './EmbeddableContact';
import { EmbeddableLocation } from './EmbeddableLocation';
import { ShipmentStopType } from './shipment/ShipmentStopType';
import { ShippingPoint } from './ShippingPoint';

const TYPE = 'ShipOrder';

export class ShipOrderShippingPoint implements ShippingPoint {
  id?: number;
  shippingPointType: ShipmentStopType.PICKUP | ShipmentStopType.DROP_OFF;
  location: EmbeddableLocation = new EmbeddableLocation();
  contact: EmbeddableContact = new EmbeddableContact();
  specialInstructions?: string;
  appointmentRequired?: boolean = false;
  requestedAppointmentEarliest?: DateTime;
  requestedAppointmentLatest?: DateTime;
  requestedAccessorials: AccessorialCode[] = [];

  constructor(opts?: Partial<ShipOrderShippingPoint>) {
    if (opts?.id != null) {
      this.id = opts.id;
    }
    this.shippingPointType = opts?.shippingPointType ?? ShipmentStopType.PICKUP;
    if (opts?.location != null) {
      this.location = new EmbeddableLocation(opts.location);
    }
    if (opts?.contact != null) {
      this.contact = new EmbeddableContact(opts.contact);
    }
    if (opts?.specialInstructions != null) {
      this.specialInstructions = opts.specialInstructions;
    }
    if (opts?.appointmentRequired != null) {
      this.appointmentRequired = opts.appointmentRequired;
    }
    this.requestedAppointmentEarliest = loadLuxonDateTime(
      opts?.requestedAppointmentEarliest,
      opts?.location?.timeZone,
    );
    this.requestedAppointmentLatest = loadLuxonDateTime(
      opts?.requestedAppointmentLatest,
      opts?.location?.timeZone,
    );
    if (opts?.requestedAccessorials != null) {
      this.requestedAccessorials = opts.requestedAccessorials.map(
        (val) => new AccessorialCode(val),
      );
    }
  }

  static isShipOrderShippingPoint(
    location: ShippingPoint,
  ): location is ShipOrderShippingPoint {
    return location.type === TYPE;
  }

  get type() {
    return TYPE;
  }

  get isInProgress() {
    return false;
  }

  get stopType() {
    return this.shippingPointType;
  }

  get isPickup() {
    return this.stopType === ShipmentStopType.PICKUP;
  }

  get isDropOff() {
    return this.stopType === ShipmentStopType.DROP_OFF;
  }

  get requestedAppointment(): ZonedDateTimeRange {
    return new ZonedDateTimeRange(
      {
        start: this.requestedAppointmentEarliest,
        end: this.requestedAppointmentLatest,
      },
      this.location?.timeZone,
    );
  }

  get sequenceNumber() {
    return this.stopType === ShipmentStopType.PICKUP ? 1 : 2;
  }

  get stopLabel(): string {
    const stopName = [
      this.location.locationName,
      this.location.city,
      this.location.state,
    ]
      .filter((x) => !!x?.trim())
      .join(', ');

    const stopNumber = i18n.global.t('common.paramNum', {
      param: i18n.global.t('shipments.stops'),
      num: this.sequenceNumber,
    });

    return [stopNumber, stopName].join(' - ');
  }

  isResolved(_requireBothDate: boolean = false) {
    return false;
  }
}
